import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Note } from '@rossum/api-client/notes';
import { CircleIconWrapper, DetailDrawer, EmptyStateRadar } from '@rossum/ui';
import {
  IconMessages,
  IconMessagesOff,
  IconSend2,
} from '@rossum/ui/icons/tabler';
import {
  Badge,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  SvgIcon,
} from '@rossum/ui/material';
import { Annotation } from '../../../../types/annotation';
import { useDocumentCommentsFlags } from '../../core';
import { useCreateComment } from '../hooks/useCreateComment';
import { CommentsThread } from './CommentsThread';
import { CommentTextarea } from './CommentTextarea';

type CommentsDrawerProps = {
  open: boolean;
  onClose: () => void;
  annotation: Pick<Annotation, 'url'>;
  comments: Note[] | undefined;
  isLoading: boolean;
};

export const CommentsDrawer = ({
  open,
  onClose,
  annotation,
  comments,
  isLoading,
}: CommentsDrawerProps) => {
  const intl = useIntl();
  const [comment, setComment] = useState('');
  const { mutate: createComment, isLoading: isCreatingComment } =
    useCreateComment();

  const { hasWriteAcess } = useDocumentCommentsFlags();

  const handleSubmitComment = () => {
    if (!comment) return;

    createComment(
      {
        annotation: annotation.url,
        content: comment,
        type: 'comment',
      },
      {
        onSuccess: () => setComment(''),
      }
    );
  };

  return (
    <DetailDrawer
      open={open}
      onClose={onClose}
      titleIcon={
        <CircleIconWrapper size={36}>
          <Badge
            variant="standard"
            badgeContent={comments?.length ?? 0}
            color="secondary"
            slotProps={{
              badge: {
                sx: {
                  height: 16,
                  width: 16,
                  minWidth: 0,
                  px: 0,
                  fontSize: '1rem',
                },
              },
            }}
            max={99}
          >
            <SvgIcon>
              <IconMessages />
            </SvgIcon>
          </Badge>
        </CircleIconWrapper>
      }
      title={intl.formatMessage({ id: 'components.documentComments.title' })}
      size="small"
    >
      <Stack sx={{ flexGrow: 1 }} spacing={1} divider={<Divider />} useFlexGap>
        {isLoading || isCreatingComment ? <LinearProgress /> : null}
        {hasWriteAcess ? (
          <Stack
            direction="row"
            sx={{ py: 2, px: 3, alignItems: 'center' }}
            spacing={1}
            useFlexGap
          >
            <CommentTextarea
              onSubmit={handleSubmitComment}
              onChange={e => setComment(e.target.value)}
              value={comment}
              placeholder={intl.formatMessage({
                id: 'components.documentComments.composer.placeholder',
              })}
              disabled={isCreatingComment}
              sx={{ flexGrow: 1 }}
            />
            <IconButton
              variant="contained"
              color="primary"
              onClick={handleSubmitComment}
              disabled={!comment || isCreatingComment}
            >
              <SvgIcon>
                <IconSend2 />
              </SvgIcon>
            </IconButton>
          </Stack>
        ) : null}
        <Stack
          sx={{
            flexGrow: 1,
            flexShrink: 0,
            py: 3,
            overflow: 'hidden',
          }}
        >
          {comments && comments.length > 0 ? (
            <CommentsThread comments={comments} hasWriteAcess={hasWriteAcess} />
          ) : (
            <EmptyStateRadar
              title={intl.formatMessage({
                id: 'components.documentComments.emptyState.title',
              })}
              description={intl.formatMessage({
                id: 'components.documentComments.emptyState.description',
              })}
              iconProps={{ Icon: IconMessagesOff, iconSize: 36 }}
            />
          )}
        </Stack>
      </Stack>
    </DetailDrawer>
  );
};
